import { useEffect, useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Comprador, type CompradorDetalle, type CompradorListado } from "@/lib/api";
import { aPayloadComprador, formComprador, validarComprador, type ErroresComprador, type FormComprador } from "@/lib/ventas";

type Dialogo = { tipo: "ficha"; id: number } | { tipo: "form"; comprador: Comprador | null } | { tipo: "borrar"; comprador: CompradorListado } | null;
const SIN_ERRORES: ErroresComprador = {};

/** Lógica del listado de compradores (sin JSX). Admin da de alta, edita y borra; contador y escribano miran. */
export function useCompradoresTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/compradores`;
  const esAdmin = membresia.rol === "admin";

  // La búsqueda pide al backend 300 ms después de la última tecla (mismo patrón que useVistaPrevia).
  const [q, setQ] = useState("");
  const [qBuscado, setQBuscado] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQBuscado(q.trim()), 300);
    return () => clearTimeout(timer);
  }, [q]);
  const listado = useApiResource<CompradorListado[]>(`${base}${qBuscado ? `?q=${encodeURIComponent(qBuscado)}` : ""}`);

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const detalle = useApiResource<CompradorDetalle>(dialogo?.tipo === "ficha" ? `${base}/${dialogo.id}` : null);
  // useApiResource conserva la respuesta anterior: sólo se muestra si es de este comprador y ya terminó de cargar.
  const ficha = dialogo?.tipo === "ficha" && !detalle.loading && detalle.data?.comprador.id === dialogo.id ? detalle.data : null;

  const [form, setForm] = useState<FormComprador>(() => formComprador());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const accionGuardar = useAsyncAction((id: number | null, f: FormComprador) =>
    id === null ? api.post<Comprador>(base, aPayloadComprador(f)) : api.patch<Comprador>(`${base}/${id}`, aPayloadComprador(f)),
  );
  const accionBorrar = useAsyncAction((id: number) => api.delete<void>(`${base}/${id}`).then(() => true));
  const errores = validarComprador(form);
  const cerrar = () => setDialogo(null);

  const abrirForm = (comprador: Comprador | null) => {
    setForm(formComprador(comprador ?? undefined));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", comprador });
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "form" || Object.keys(errores).length > 0) return;
    if (await accionGuardar.run(dialogo.comprador?.id ?? null, form)) {
      cerrar();
      listado.recargar();
    }
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    if (await accionBorrar.run(dialogo.comprador.id)) {
      cerrar();
      listado.recargar();
    }
  };

  return {
    esAdmin,
    items: listado.data ?? [],
    q,
    setQ,
    cargando: listado.loading && !listado.data,
    errorCarga: listado.data ? null : (listado.error?.message ?? null),
    // Si ya había un listado y falla una búsqueda nueva, se sigue mostrando el anterior con el error arriba.
    errorBusqueda: listado.data ? (listado.error?.message ?? null) : null,
    reintentar: listado.recargar,
    dialogo,
    cerrar,
    verFicha: (id: number) => setDialogo({ tipo: "ficha", id }),
    ficha,
    errorFicha: dialogo?.tipo === "ficha" ? (detalle.error?.message ?? null) : null,
    abrirNuevo: () => abrirForm(null),
    abrirEditar: (c: Comprador) => abrirForm(c),
    abrirBorrar: (c: CompradorListado) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar", comprador: c });
    },
    form,
    cambiar: <K extends keyof FormComprador>(campo: K, valor: FormComprador[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}
